import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  DatePicker, 
  Select, 
  Tabs, 
  Typography, 
  Spin,
  Button,
  Space,
  message,
  Table,
  Tag
} from 'antd'
import {
  ShoppingCartOutlined,
  DollarOutlined,
  UserOutlined,
  TeamOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  ExportOutlined,
  BarChartOutlined,
  PieChartOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'

dayjs.extend(weekOfYear)
import type { DatePickerProps } from 'antd'
import { apiService } from '@services/apiService'

const { Title, Text } = Typography
const { RangePicker } = DatePicker
const { Option } = Select
const { TabPane } = Tabs

interface OverviewData {
  totalOrders: number
  totalAmount: number
  newUsers: number
  activeUsers: number
}

interface OrderAnalysis {
  completionRate: number
  avgAmount: number
  peakHour: string
}

interface UserData {
  totalUsers: number
  userGrowth: number
  newUsers: number
  newUserGrowth: number
}

interface UserSourceData {
  source: string
  percentage: number
  color: string
}

interface TopProduct {
  id: string
  name: string
  gardenName: string
  sales: number
  amount: number
}

interface OrderChartData {
  date: string
  label: string
  value: number
  heightPercent: number
}

const LIST_PAGE = 10000

function unwrapList(payload: any, nestedKeys: string[]): any[] {
  const d = payload?.data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object') {
    if (Array.isArray(d.data)) return d.data
    for (const k of nestedKeys) {
      if (Array.isArray(d[k])) return d[k]
    }
  }
  return []
}

const Statistics: React.FC = () => {
  // 日期选择
  const [currentMonth, setCurrentMonth] = useState(dayjs())
  const [orderTimeRange, setOrderTimeRange] = useState<'day' | 'week' | 'month'>('week')
  
  // 加载状态
  const [loading, setLoading] = useState({
    overview: true,
    orderChart: true,
    userChart: true,
    products: true
  })
  
  // 数据概览
  const [overview, setOverview] = useState<OverviewData>({
    totalOrders: 0,
    totalAmount: 0,
    newUsers: 0,
    activeUsers: 0
  })
  
  // 订单图表数据
  const [orderData, setOrderData] = useState<OrderChartData[]>([])
  const [orderYMax, setOrderYMax] = useState(0)
  
  // 订单分析数据
  const [orderAnalysis, setOrderAnalysis] = useState<OrderAnalysis>({
    completionRate: 0,
    avgAmount: 0,
    peakHour: '--'
  })
  
  // 用户数据
  const [userData, setUserData] = useState<UserData>({
    totalUsers: 0,
    userGrowth: 0,
    newUsers: 0,
    newUserGrowth: 0
  })
  
  // 用户来源数据
  const [userSourceData, setUserSourceData] = useState<UserSourceData[]>([])
  
  // 热销商品Top5
  const [topProducts, setTopProducts] = useState<TopProduct[]>([])

  // 加载统计数据
  const loadStatisticsData = async () => {
    try {
      setLoading(prev => ({ ...prev, overview: true, orderChart: true, userChart: true, products: true }))
      
      // 加载概览数据
      await loadOverviewData()
      
      // 加载订单数据
      await loadOrderData()
      
      // 加载用户数据
      await loadUserData()
      
      // 加载商品数据
      await loadProductData()
      
    } catch (error) {
      console.error('加载统计数据错误:', error)
      message.error('加载统计数据失败')
    }
  }

  // 加载概览数据
  const loadOverviewData = async () => {
    try {
      const q = `page=1&pageSize=${LIST_PAGE}`
      const [usersData, subscriptionsData, gardenStats] = await Promise.all([
        apiService.getEnvelope(`/users?${q}`),
        apiService.getEnvelope(`/subscriptions?${q}`),
        apiService.getEnvelope('/gardens/statistics'),
      ])

      if (usersData.success && subscriptionsData.success) {
        const users = unwrapList(usersData, ['users'])
        const subscriptions = unwrapList(subscriptionsData, ['subscriptions'])

        const totalOrders = subscriptions.length
        // 使用菜园统计接口的 totalRevenue，与菜园管理页保持一致
        const totalAmount = (gardenStats as any)?.data?.totalRevenue
          ?? subscriptions.reduce(
            (total: number, sub: any) => total + Number(sub.totalAmount ?? 0),
            0
          )

        const totalUsers =
          typeof usersData.total === 'number' ? usersData.total : users.length

        const currentMonthStr = currentMonth.format('YYYY-MM')
        const newUsers = users.filter((user: any) => {
          const t = user.createdAt || user.createTime || user.registerTime
          return t && dayjs(t).format('YYYY-MM') === currentMonthStr
        }).length

        const activeUsers = new Set(
          subscriptions
            .filter(
              (sub: any) =>
                sub.status === 'ACTIVE' || sub.status === 'active'
            )
            .map((sub: any) => sub.userId)
        ).size

        setOverview({
          totalOrders,
          totalAmount,
          newUsers,
          activeUsers,
        })
      }

      setLoading((prev) => ({ ...prev, overview: false }))
    } catch (error) {
      console.error('加载概览数据错误:', error)
      setLoading((prev) => ({ ...prev, overview: false }))
    }
  }

  // 加载订单数据
  const loadOrderData = async () => {
    try {
      const subsJson = await apiService.getEnvelope(
        `/subscriptions?page=1&pageSize=${LIST_PAGE}`
      )
      const subscriptions = subsJson.success
        ? unwrapList(subsJson, ['subscriptions'])
        : []

      const now = dayjs()
      const chartRows: OrderChartData[] = []

      for (let i = 6; i >= 0; i--) {
        const anchor = now.subtract(6 - i, orderTimeRange === 'day' ? 'day' : orderTimeRange === 'week' ? 'week' : 'month')
        let label: string
        if (orderTimeRange === 'day') {
          label = anchor.format('MM/DD')
        } else if (orderTimeRange === 'week') {
          label = `第${anchor.week()}周`
        } else {
          label = anchor.format('MM月')
        }

        const value = subscriptions.filter((sub: any) => {
          const t = dayjs(sub.createdAt || sub.subscriptionDate)
          if (orderTimeRange === 'day') {
            return t.format('YYYY-MM-DD') === anchor.format('YYYY-MM-DD')
          }
          if (orderTimeRange === 'week') {
            return t.isSame(anchor, 'week')
          }
          return t.format('YYYY-MM') === anchor.format('YYYY-MM')
        }).length

        chartRows.push({
          date: anchor.format('YYYY-MM-DD'),
          label,
          value,
          heightPercent: 0,
        })
      }

      const maxValue = Math.max(1, ...chartRows.map((d) => d.value))
      const yMax = Math.ceil(maxValue / 10) * 10 || 10
      chartRows.forEach((item) => {
        item.heightPercent = (item.value / yMax) * 100
      })

      setOrderData(chartRows)
      setOrderYMax(yMax)

      const total = subscriptions.length
      const active = subscriptions.filter(
        (s: any) => s.status === 'ACTIVE' || s.status === 'active'
      ).length
      const completionRate =
        total === 0 ? 0 : Math.round((active / total) * 100)
      const sum = subscriptions.reduce(
        (acc: number, s: any) => acc + Number(s.totalAmount ?? s.monthlyPrice ?? 0),
        0
      )
      const avgAmount = total === 0 ? 0 : Math.round(sum / total)

      setOrderAnalysis({
        completionRate,
        avgAmount,
        peakHour: '--',
      })

      setLoading((prev) => ({ ...prev, orderChart: false }))
    } catch (error) {
      console.error('加载订单数据错误:', error)
      setLoading((prev) => ({ ...prev, orderChart: false }))
    }
  }

  // 加载用户数据
  const loadUserData = async () => {
    try {
      const usersData = await apiService.getEnvelope(
        `/users?page=1&pageSize=${LIST_PAGE}`
      )

      if (usersData.success) {
        const users = unwrapList(usersData, ['users'])
        const totalUsers =
          typeof usersData.total === 'number' ? usersData.total : users.length

        const currentMonthStr = currentMonth.format('YYYY-MM')
        const prevMonthStr = currentMonth.subtract(1, 'month').format('YYYY-MM')
        const newUsers = users.filter((user: any) => {
          const t = user.createdAt || user.createTime || user.registerTime
          return t && dayjs(t).format('YYYY-MM') === currentMonthStr
        }).length
        const prevNew = users.filter((user: any) => {
          const t = user.createdAt || user.createTime || user.registerTime
          return t && dayjs(t).format('YYYY-MM') === prevMonthStr
        }).length

        const pct = (cur: number, prev: number) => {
          if (prev <= 0) return cur > 0 ? 100 : 0
          return Math.round(((cur - prev) / prev) * 100)
        }
        const userGrowth = pct(newUsers, prevNew)
        const newUserGrowth = userGrowth

        setUserData({
          totalUsers,
          userGrowth,
          newUsers,
          newUserGrowth,
        })

        const provinceCount: Record<string, number> = {}
        users.forEach((u: any) => {
          const p = u.province?.trim() || '未填写地区'
          provinceCount[p] = (provinceCount[p] || 0) + 1
        })
        const total = users.length || 1
        const palette = ['#4CAF50', '#2196F3', '#FFC107', '#9C27B0', '#00BCD4', '#E91E63']
        const sources = Object.entries(provinceCount)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6)
          .map(([source, c], i) => ({
            source,
            percentage: Math.round((c / total) * 100),
            color: palette[i % palette.length],
          }))
        setUserSourceData(sources)
      }

      setLoading((prev) => ({ ...prev, userChart: false }))
    } catch (error) {
      console.error('加载用户数据错误:', error)
      setLoading((prev) => ({ ...prev, userChart: false }))
    }
  }

  // 加载商品数据
  const loadProductData = async () => {
    try {
      const subsJson = await apiService.getEnvelope(
        `/subscriptions?page=1&pageSize=${LIST_PAGE}`
      )
      const subscriptions = subsJson.success
        ? unwrapList(subsJson, ['subscriptions'])
        : []

      const byGarden: Record<
        string,
        { id: string; name: string; gardenName: string; sales: number; amount: number }
      > = {}

      subscriptions.forEach((sub: any) => {
        const gid = sub.gardenId || 'unknown'
        const gname = sub.garden?.name || '未知菜园'
        if (!byGarden[gid]) {
          byGarden[gid] = {
            id: gid,
            name: sub.planType || '订阅套餐',
            gardenName: gname,
            sales: 0,
            amount: 0,
          }
        }
        byGarden[gid].sales += 1
        byGarden[gid].amount += Number(sub.totalAmount ?? 0)
      })

      const topProducts: TopProduct[] = Object.values(byGarden)
        .sort((a, b) => b.sales - a.sales)
        .slice(0, 5)

      setTopProducts(topProducts)

      setLoading((prev) => ({ ...prev, products: false }))
    } catch (error) {
      console.error('加载商品数据错误:', error)
      setLoading((prev) => ({ ...prev, products: false }))
    }
  }

  // 月份选择处理
  const onMonthChange: DatePickerProps['onChange'] = (date) => {
    if (date) {
      setCurrentMonth(date)
      loadStatisticsData()
    }
  }

  // 订单时间范围切换
  const switchOrderTimeRange = (range: 'day' | 'week' | 'month') => {
    setOrderTimeRange(range)
    loadOrderData()
  }

  // 导出统计数据
  const exportStatistics = () => {
    message.success('统计数据导出功能开发中...')
  }

  // 查看完整销售报表
  const navigateToSalesReport = () => {
    message.info('销售报表功能开发中...')
  }

  useEffect(() => {
    loadStatisticsData()
  }, [])

  return (
    <div>
      {/* 标题和时间选择 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>数据统计</Title>
        <Space>
          <DatePicker
            picker="month"
            value={currentMonth}
            onChange={onMonthChange}
            format="YYYY年MM月"
            style={{ width: 150 }}
          />
          <Button icon={<ExportOutlined />} onClick={exportStatistics}>
            导出数据
          </Button>
        </Space>
      </div>

      {/* 数据概览卡片 */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Title level={4} style={{ margin: 0 }}>数据概览</Title>
          <Text type="secondary">{currentMonth.format('YYYY年MM月')}数据</Text>
        </div>
        
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} lg={6}>
            <Spin spinning={loading.overview}>
              <Statistic
                title="订单总量"
                value={overview.totalOrders}
                prefix={<ShoppingCartOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Spin>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Spin spinning={loading.overview}>
              <Statistic
                title="交易金额"
                value={overview.totalAmount}
                prefix={<DollarOutlined />}
                precision={2}
                valueStyle={{ color: '#cf1322' }}
              />
            </Spin>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Spin spinning={loading.overview}>
              <Statistic
                title="新增用户"
                value={overview.newUsers}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Spin>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Spin spinning={loading.overview}>
              <Statistic
                title="活跃用户"
                value={overview.activeUsers}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Spin>
          </Col>
        </Row>
      </Card>

      {/* 订单数据统计 */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Title level={4} style={{ margin: 0 }}>订单数据</Title>
          <Space>
            <Button 
              type={orderTimeRange === 'day' ? 'primary' : 'default'}
              onClick={() => switchOrderTimeRange('day')}
              size="small"
            >
              日
            </Button>
            <Button 
              type={orderTimeRange === 'week' ? 'primary' : 'default'}
              onClick={() => switchOrderTimeRange('week')}
              size="small"
            >
              周
            </Button>
            <Button 
              type={orderTimeRange === 'month' ? 'primary' : 'default'}
              onClick={() => switchOrderTimeRange('month')}
              size="small"
            >
              月
            </Button>
          </Space>
        </div>
        
        {/* 订单统计图表 */}
        <div style={{ marginBottom: '24px' }}>
          <Spin spinning={loading.orderChart}>
            <div style={{ height: '200px', display: 'flex', alignItems: 'end', justifyContent: 'space-around', padding: '20px 0', border: '1px solid #f0f0f0', borderRadius: '6px' }}>
              {orderData.map((item, index) => (
                <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '60px' }}>
                  <div 
                    style={{ 
                      width: '30px', 
                      height: `${item.heightPercent}%`, 
                      backgroundColor: '#1890ff', 
                      borderRadius: '4px 4px 0 0',
                      minHeight: '20px',
                      marginBottom: '8px'
                    }}
                  />
                  <Text style={{ fontSize: '12px' }}>{item.label}</Text>
                  <Text style={{ fontSize: '10px', color: '#999' }}>{item.value}</Text>
                </div>
              ))}
            </div>
          </Spin>
        </div>
        
        {/* 订单分析数据 */}
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="订单完成率"
                value={orderAnalysis.completionRate}
                suffix="%"
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="客单价"
                value={orderAnalysis.avgAmount}
                prefix="¥"
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="高峰时段"
                value={orderAnalysis.peakHour}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* 用户数据统计 */}
      <Card style={{ marginBottom: '24px' }}>
        <Title level={4} style={{ marginBottom: '24px' }}>用户数据</Title>
        
        <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12}>
            <Card size="small">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary">用户总量</Text>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '8px' }}>
                    {userData.totalUsers}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: userData.userGrowth >= 0 ? '#3f8600' : '#cf1322', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {userData.userGrowth >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    <Text style={{ color: userData.userGrowth >= 0 ? '#3f8600' : '#cf1322' }}>
                      {userData.userGrowth}%
                    </Text>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card size="small">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary">本月新增</Text>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '8px' }}>
                    {userData.newUsers}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: userData.newUserGrowth >= 0 ? '#3f8600' : '#cf1322', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {userData.newUserGrowth >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    <Text style={{ color: userData.newUserGrowth >= 0 ? '#3f8600' : '#cf1322' }}>
                      {userData.newUserGrowth}%
                    </Text>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
        
        <div style={{ marginBottom: '16px' }}>
          <Text strong>用户来源分布</Text>
        </div>
        
        <Spin spinning={loading.userChart}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '40px' }}>
            {/* 简化的饼图 */}
            <div style={{ width: '120px', height: '120px', borderRadius: '50%', background: 'conic-gradient(#4CAF50 0deg 162deg, #2196F3 162deg 252deg, #FFC107 252deg 324deg, #9C27B0 324deg 360deg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundColor: 'white' }} />
            </div>
            
            {/* 图例 */}
            <div style={{ flex: 1 }}>
              {userSourceData.map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <div 
                    style={{ 
                      width: '12px', 
                      height: '12px', 
                      backgroundColor: item.color, 
                      borderRadius: '2px',
                      marginRight: '8px' 
                    }} 
                  />
                  <Text style={{ marginRight: '8px', minWidth: '80px' }}>{item.source}</Text>
                  <Text>{item.percentage}%</Text>
                </div>
              ))}
            </div>
          </div>
        </Spin>
      </Card>

      {/* 商品数据统计 */}
      <Card>
        <Title level={4} style={{ marginBottom: '24px' }}>商品数据</Title>
        
        <div style={{ marginBottom: '16px' }}>
          <Text strong>热销商品Top5</Text>
        </div>
        
        <Spin spinning={loading.products}>
          <div style={{ marginBottom: '16px' }}>
            {topProducts.map((product, index) => (
              <div key={product.id} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '12px 0', 
                borderBottom: index < topProducts.length - 1 ? '1px solid #f0f0f0' : 'none' 
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  backgroundColor: index < 3 ? '#ff4d4f' : '#d9d9d9', 
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: '12px', 
                  fontWeight: 'bold',
                  marginRight: '12px'
                }}>
                  {index + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{product.name}</div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>{product.gardenName}</Text>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{product.sales}件</div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>¥{product.amount}</Text>
                </div>
              </div>
            ))}
          </div>
        </Spin>
        
        <Button type="link" onClick={navigateToSalesReport} style={{ padding: 0 }}>
          查看完整销售报表 &gt;
        </Button>
      </Card>
    </div>
  )
}

export default Statistics